import { useCallback, useEffect, useRef } from 'react';

import { alphabet, getFocusableArray } from './selectors';

const useFocusable = (
    entry: boolean,
    onExit: () => void,
    useExtraKeyboardFocusCommands: boolean,
    disableEscapeKeyDown: boolean,
) => {
    const focusContainer = useRef<HTMLDivElement | null>(null);

    const onKeyPress = useCallback(
        (evt: KeyboardEvent) => {
            if (evt.key === 'Escape' && !disableEscapeKeyDown) {
                evt.stopPropagation();
                onExit();
                return;
            }
            if (focusContainer.current) {
                const { activeElement } = document;
                if ((activeElement as HTMLElement) === document.body) {
                    focusContainer.current.focus();
                }
                const elementsToFocus = getFocusableArray(focusContainer.current);
                const focusedItem = elementsToFocus.find((el) => el === activeElement);

                const numberOfFocusableItems = elementsToFocus.length;

                const focusedItemIndex = focusedItem ? elementsToFocus.indexOf(focusedItem) : 0;
                const isOnFirstElement = focusedItemIndex === 0;
                const isOnLastElement = focusedItemIndex === numberOfFocusableItems - 1;
                const lastElementIndex = numberOfFocusableItems - 1;

                if (useExtraKeyboardFocusCommands) {
                    if (evt.shiftKey || evt.altKey || evt.ctrlKey || evt.metaKey) {
                        return;
                    }
                    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
                        evt.stopPropagation();
                        evt.preventDefault();
                        if (isOnFirstElement) {
                            elementsToFocus[lastElementIndex].focus();
                            return;
                        }
                        elementsToFocus[focusedItemIndex - 1].focus();
                        return;
                    }
                    if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
                        evt.stopPropagation();
                        evt.preventDefault();
                        if (isOnLastElement) {
                            elementsToFocus[0].focus();
                            return;
                        }
                        elementsToFocus[focusedItemIndex + 1].focus();
                        return;
                    }
                    if (evt.key === 'Home') {
                        evt.stopPropagation();
                        evt.preventDefault();
                        elementsToFocus[0].focus();
                        return;
                    }

                    if (evt.key === 'End') {
                        evt.stopPropagation();
                        evt.preventDefault();
                        elementsToFocus[lastElementIndex].focus();
                        return;
                    }

                    if (alphabet.includes(evt.key)) {
                        evt.preventDefault();
                        evt.stopPropagation();
                        const elementToFocus = elementsToFocus.find((x) =>
                            x.textContent?.toLowerCase().startsWith(evt.key),
                        );
                        if (elementToFocus) {
                            elementToFocus.focus();
                        }
                    }
                }
            }
        },
        [focusContainer, onExit, useExtraKeyboardFocusCommands, disableEscapeKeyDown],
    );

    useEffect(() => {
        const container = focusContainer.current;
        if (entry && container) {
            container.addEventListener('keydown', onKeyPress);
        }

        return () => container?.removeEventListener('keydown', onKeyPress);
    }, [onKeyPress, entry, focusContainer]);

    return focusContainer;
};

export default useFocusable;
